<template>
  <div class="transaction-chart-container" ref="mainBox" :class="{ 'new-layout': newLayout }">
    <div class="button-panel">
      <div class="txType-btn" :class="{ sell: txType === 'S' }" @click="showSellTx()">
        {{ $t('transaction.SOLD') }}
      </div>
      <div class="txType-btn" :class="{ rent: txType === 'L' }" @click="showRentTx()">
        {{ $t('transaction.LEASED') }}
      </div>
    </div>
    <vue-c3 :handler="handler" ref="chartBox"></vue-c3>
  </div>
</template>
<script>
/* eslint-disable */
import Vue from 'vue';
import VueC3 from 'vue-c3';
import 'c3/c3.min.css';
import Moment from 'moment';
import * as d3 from 'd3-format';
import gql from 'graphql-tag';
import titleIcon from '@/assets/icons/icon_TxChart.svg';
import SectionHeader from '@/components/SectionHeader';

export default {
  props: ['buildingId', 'ics', 'newLayout'],
  components: {
    VueC3,
  },
  data() {
    return {
      handler: new Vue(),
      formattedData: {},
      maxDisplayMonths: 24,
      txType: 'S',
      valueColor: '#3b3b3b',
      transaction: [],
      loadingTransaction: 0,
    };
  },
  mounted() {
    this.updateGraph(this.chartData);
  },
  watch: {
    txType(value) {
      this.$emit('txChanged', value);
      this.updateGraph(this.chartData);
    },
    loadingTransaction(value) {
      if (!value) {
        this.updateGraph(this.chartData);
      }
    },
  },
  computed: {
    id() {
      return this.buildingId;
    },
    chartData() {
      return this.formatTxData(this.transaction, this.txType);
    },
    chartDataS() {
      return this.formatTxData(this.transaction, 'S');
    },
    chartDataL() {
      return this.formatTxData(this.transaction, 'L');
    },
    numTxColor() {
      if (this.newLayout) return this.txType === 'S' ? '#f6571a' : '#009342';
      return this.txType === 'S' ? '#f52c25' : '#1fa39d';
    },
    tickRotate() {
      return window.innerWidth < 600 ? 75 : 0;
    },
  },
  methods: {
    showSellTx() {
      this.txType = 'S';
    },
    showRentTx() {
      this.txType = 'L';
    },
    formatTxData(d, tx_type) {
      let data = d.filter(item => this.txType === item.tx_type);
      let tmp = {};
      data.forEach(function(item) {
        const key = item.date_key;
        tmp[key] = item;
      });
      data = { ...tmp };

      let columns = {};
      const initialDateKey = Moment()
        .subtract(5, 'years')
        .format('YYYYMM');
      let firstKey = Object.keys(data)[0];
      const endKey = Moment().format('YYYYMM');
      if (Moment(firstKey, 'YYYYMM') > Moment(initialDateKey, 'YYYYMM')) {
        firstKey = initialDateKey;
      }
      let i = Moment(firstKey, 'YYYYMM')
        .subtract(1, 'months')
        .format('YYYYMM');

      while (i < endKey) {
        const next = Moment(i, 'YYYYMM')
          .add(1, 'months')
          .format('YYYYMM');
        if (next in data) {
          columns[next] = {
            ...data[next],
          };
        } else {
          if (columns[i]) {
            columns[next] = {
              ...columns[i],
              date_key: parseInt(next, 10),
              num_of_tx: 0,
              value: columns[i].value ? columns[i].value : 0,
              tx_type: columns[i].tx_type ? columns[i].tx_type : tx_type,
            };
          } else {
            columns[next] = {
              date_key: parseInt(next, 10),
              num_of_tx: 0,
              value: 0,
              tx_type: tx_type,
            };
          }
        }
        i = next;
      }

      const result = [];
      let keys = Object.keys(columns);
      keys.sort((a, b) => {
        return a - b;
      });
      keys = keys.filter(
        item =>
          item >=
          Moment()
            .subtract(this.maxDisplayMonths, 'months')
            .format('YYYYMM'),
      );
      keys.forEach(colKey => {
        const { __typename, ...newItem } = columns[colKey];
        result.push({
          ...newItem,
          date_key: Moment(newItem.date_key, 'YYYYMM').format('YYYY-MM-DD'),
        });
      });
      return result;
    },

    updateGraph(d) {
      const options = {
        data: {
          json: d,
          keys: {
            x: 'date_key', // it's possible to specify 'x' when category axis
            value: ['num_of_tx', 'value'],
          },
          types: {
            num_of_tx: 'bar',
          },
          axes: {
            num_of_tx: 'y',
            value: 'y2',
          },
          names: {
            num_of_tx: this.$t('transaction.NUM_OF_TX'),
            value: this.$t('transaction.PRICE_AVG'),
          },
          colors: {
            value: this.valueColor,
            num_of_tx: this.numTxColor,
          },
          empty: {
            label: {
              text: this.$t('transaction.NO_TX_MSG'),
            },
          },
        },
        axis: {
          x: {
            type: 'timeseries',
            tick: {
              rotate: this.tickRotate,
              fit: true,
              culling: false,
              format: '%Y/%m',
            },
          },
          y: {
            type: 'indexed',
            tick: {
              format: function(x) {
                return x % 1 === 0 ? d3.format('宗<')(x) : '';
              },
            },
            label: {
              text: this.$t('transaction.NUM_OF_TX'),
              position: 'outer-middle',
            },
            padding: {
              top: 200,
              bottom: 100,
            },
          },
          y2: {
            show: true,
            tick: {
              format: d3.format('$,.0f'),
            },
            label: {
              text: this.$t('transaction.PRICE_AVG'),
              position: 'outer-middle',
            },
          },
        },
      };
      this.handler.$emit('init', options);
    },
  },
  apollo: {
    $client: 'api',
    transaction: {
      fetchPolicy: 'network-only',
      query: gql`
        query($pisBuildingId: ID!) {
          txC: transactionChart(company: MR, ics: COMM, buildingId: $pisBuildingId) {
            date_key
            tx_type
            building_id
            value
            num_of_tx
          }
          txI: transactionChart(company: MR, ics: IND, buildingId: $pisBuildingId) {
            date_key
            tx_type
            building_id
            value
            num_of_tx
          }
          txS: transactionChart(company: MR, ics: SHOP, buildingId: $pisBuildingId) {
            date_key
            tx_type
            building_id
            value
            num_of_tx
          }
        }
      `,
      variables() {
        return { pisBuildingId: this.buildingId };
      },
      update(data) {
        switch (this.ics) {
          case 'i':
            return data.txI;
            break;
          case 'c':
            return data.txC;
            break;
          case 's':
            return data.txS;
            break;
          default:
            if (data.txC.length) {
              return data.txC;
            } else if (data.txS.length) {
              return data.txS;
            } else if (data.txI.length) {
              return data.txI;
            }
            return null;
            break;
        }
      },
      result({ data }) {
        if (this.chartDataS.length === 0) {
          this.showRentTx();
        } else {
          this.showSellTx();
        }
      },
      loadingKey: 'loadingTransaction',
    },
  },
};
</script>
<style>
.c3-circle {
  stroke: #242020;
  stroke-width: 2px;
}
.c3-circle._expanded_ {
  stroke: #242020;
  stroke-width: 4px;
}
.c3-axis-y-label {
  font-size: 15px;
}
.c3-axis-y2-label {
  font-size: 15px;
}
</style>
<style scoped>
.transaction-chart-container {
  min-height: 320px;
}
.title {
  text-align: center;
  text-decoration: underline;
  font-size: 17px;
  margin-top: 20px;
}
.button-panel {
  text-align: center;
}
.txType-btn {
  display: inline-block;
  font-size: 16px;
  color: #bbb;
  padding: 5px 10px;
  margin: 0px 10px;
  background-color: #ffffff;
  border-radius: 6px;
  border: 3px solid #bbb;
  cursor: pointer;
}

.txType-btn.sell {
  background: #f52c25;
  color: #fff;
  border-color: #f52c25;
}

.txType-btn.rent {
  background: #1fa39d;
  color: #fff;
  border-color: #1fa39d;
}
.new-layout .txType-btn.sell {
  background: #f6571a;
  border-color: #f6571a;
}

.new-layout .txType-btn.rent {
  background: #009342;
  border-color: #009342;
}

.new-layout .txType-btn {
  border-radius: 4px;
  border: 1px solid #bbb;
}
</style>
